const runningNextFrameCacheMap = new Map<string, [number, number]>();

/**
 * Run a function on next animation if one is not already queue, else skip
 * @param runLater the function to run on next frame
 * @param args the function arguments
 */
export function runOnNextFrameIfNotQueued(name: string, runLater: (...args: any[]) => void, ...args: any[]) {
  if (!runningNextFrameCacheMap.has(name)) {
    runningNextFrameCacheMap.set(name, [
      1,
      requestAnimationFrame(() => {
        const nextFrame = runningNextFrameCacheMap.get(name);
        if (nextFrame != null) nextFrame[0] = 2;
        try {
          runLater(...args);
        } finally {
          runningNextFrameCacheMap.delete(name);
        }
      })
    ]);
  } else {
    const nextFrame = runningNextFrameCacheMap.get(name);
    // if the animation frame computation has not been started, cancel it and replace with the latest one
    if (nextFrame != null && nextFrame[0] === 1) {
      cancelAnimationFrame(nextFrame[1]);
      runningNextFrameCacheMap.delete(name);
      runOnNextFrameIfNotQueued(name, runLater, args);
    }
  }
}

/**
 * Run a function on everyframe for a given duration
 * @param startTime the time when the function was started from Date.now()
 * @param name the name of the timers
 * @param duration the duration in millisecond to run the function
 * @param runLater the function to run
 * @param args the arguments of the function to run
 */
export function runOnFrameForDuration(startTime: number, name: string, duration: number, runLater: (...args: any[]) => void, ...args: any[]) {
  runLater(...args);
  if (Date.now() - startTime < duration) {
    runOnNextFrameIfNotQueued(`${name} ${Date.now()}`, () => {
      runOnFrameForDuration(startTime, name, duration, runLater);
    });
  }
}

const runningTimeoutCacheMap = new Map<string, [number, NodeJS.Timeout]>();

/**
 * Run a function on next timeout if one is not already queue, else replace previous one planned
 * Avoid to launch a function two often and refresh two fast the interface, save CPU
 * @param runLater the function to run on next frame
 * @param args the function arguments
 */
export function runOnTimeoutIfNotQueued(name: string, runLater: (...args: any[]) => void, timeout: number, args?: any[]) {
  if (!runningTimeoutCacheMap.has(name)) {
    runningTimeoutCacheMap.set(name, [
      1,
      setTimeout(() => {
        const timeoutEntry = runningTimeoutCacheMap.get(name);
        if (timeoutEntry != null) {
          timeoutEntry[0] = 2;
          try {
            if (args != null && args.length > 0) {
              switch (args.length) {
                case 1:
                  runLater(args[0]);
                  console.log('runLater ', args[0]);
                  break;
                case 2:
                  runLater(args[0], args[1]);
                  break;
                case 3:
                  runLater(args[0], args[1], args[2]);
                  break;
                case 4:
                  runLater(args[0], args[1], args[2], args[3]);
                  break;
                case 5:
                  runLater(args[0], args[1], args[2], args[3], args[4]);
                  break;
              }
            } else {
              runLater();
            }
          } finally {
            runningTimeoutCacheMap.delete(name);
          }
        }
      }, timeout)
    ]);
  } else {
    const timeoutEntry = runningTimeoutCacheMap.get(name);
    // if the animation frame computation has not been started, cancel it and replace with the latest one
    if (timeoutEntry != null && timeoutEntry[0] === 1) {
      clearTimeout(timeoutEntry[1]);
      runningTimeoutCacheMap.delete(name);
      runOnTimeoutIfNotQueued(name, runLater, timeout, args);
    }
  }
}
